(function () {
  const ROOT_SELECTOR = '[data-gift-with-purchase]';
  const DATA_SELECTOR = '[data-gift-with-purchase-data]';
  const INVALID_GIFT_SELECTOR = '[data-is-gift-to-remove]';
  const GIFT_CLAIM_SELECTOR = '[data-is-gwp]';
  const INITIALIZED_ATTRIBUTE = 'data-gift-with-purchase-ready';

  const getShopifyRoot = () => window.Shopify?.routes?.root || '/';

  const escapeHTML = (value) => {
    const element = document.createElement('div');
    element.textContent = value || '';
    return element.innerHTML;
  };

  const formatMoney = (cents) => {
    const amount = Math.max(0, Number(cents) || 0);
    const format = window.themeVariables?.settings?.moneyFormat || '{{amount}}';
    const value = (amount / 100).toFixed(2).replace('.', ',');

    return format
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(amount / 100)))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, value)
      .replace(/\{\{\s*amount\s*\}\}/, (amount / 100).toFixed(2));
  };

  const formatThreshold = (cents) => {
    const amount = Math.max(0, Number(cents) || 0) / 100;

    return new Intl.NumberFormat(window.Shopify?.locale || document.documentElement.lang || 'en', {
      currency: window.Shopify?.currency?.active || 'EUR',
      maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      style: 'currency'
    }).format(amount);
  };

  const parseData = (root) => {
    const dataElement = root.querySelector(DATA_SELECTOR);

    if (!dataElement) {
      return null;
    }

    try {
      return JSON.parse(dataElement.textContent);
    } catch (error) {
      console.error('Gift with purchase data could not be parsed.', error);
      return null;
    }
  };

  const getCountryCode = () => (window.Shopify?.country || '').toLowerCase();

  const getThresholdCents = (tier) => {
    const threshold = tier?.threshold || {};
    const countryThreshold = threshold[getCountryCode()];

    if (countryThreshold === undefined || countryThreshold === null || countryThreshold === '') {
      return null;
    }

    return Number(countryThreshold) * 100;
  };

  const interpolateMessage = (message, values) => String(message || '').replace(/\{\{\s*(amount|threshold)\s*\}\}/g, (match, key) => values[key] || match);

  const showError = (message) => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      let error = root.querySelector('.gift-with-purchase__error');

      if (!error) {
        error = document.createElement('p');
        error.className = 'gift-with-purchase__error';
        error.setAttribute('role', 'alert');
        root.appendChild(error);
      }

      error.textContent = message;
    });
  };

  const getErrorMessage = () => {
    const root = document.querySelector(ROOT_SELECTOR);
    const data = root ? parseData(root) : null;

    return data?.i18n?.error_message || 'Unable to update your cart. Please try again.';
  };

  const getGiftClaimVariantCounts = () => {
    const giftElements = Array.from(document.querySelectorAll(GIFT_CLAIM_SELECTOR));
    const countedLineKeys = new Set();
    const counts = giftElements.reduce((result, element) => {
      const lineKey = element.getAttribute('data-line-key');
      const variantId = element.getAttribute('data-gift-variant-id');
      const quantity = Number(element.getAttribute('data-gift-quantity')) || 1;

      if (lineKey && countedLineKeys.has(lineKey)) {
        return result;
      }

      if (lineKey) {
        countedLineKeys.add(lineKey);
      }

      if (variantId) {
        result.set(variantId, (result.get(variantId) || 0) + quantity);
      }

      return result;
    }, new Map());

    return counts;
  };

  const createUnlockedTierElement = (tier, i18n) => {
    const element = document.createElement('div');
    element.className = 'gift-with-purchase__tier gift-with-purchase__tier--unlocked';
    element.innerHTML = `
      <div class="gift-with-purchase__product">
        ${tier.product?.image ? `<img class="gift-with-purchase__image" src="${tier.product.image}" alt="">` : ''}
        <div class="gift-with-purchase__copy">
          <p class="gift-with-purchase__product-title">${escapeHTML(tier.unlocked_message || tier.product?.title || i18n.collapsed_title)}</p>
          <button type="button" class="gift-with-purchase__button" data-gift-with-purchase-add="${tier.variant_id}">
            ${escapeHTML(i18n.button_label)}
          </button>
        </div>
      </div>
    `;

    return element;
  };

  const createLockedTierElement = (tier, thresholdCents, subtotal, i18n) => {
    const remaining = Math.max(0, thresholdCents - subtotal);
    const values = {
      amount: formatMoney(remaining),
      threshold: formatThreshold(thresholdCents)
    };
    const titleValues = {
      ...values,
      amount: values.threshold
    };
    const title = tier.locked_message || i18n.collapsed_title || '';
    const subline = i18n.locked_subline || '';
    const element = document.createElement('div');

    element.className = 'gift-with-purchase__tier gift-with-purchase__tier--locked';
    element.innerHTML = `
      <p class="gift-with-purchase__product-title">${escapeHTML(interpolateMessage(title, titleValues))}</p>
      <p class="gift-with-purchase__message">${escapeHTML(interpolateMessage(subline, values))}</p>
      <div class="gift-with-purchase__progress" aria-hidden="true">
        <span style="width: ${Math.min(100, Math.max(0, subtotal / thresholdCents * 100))}%"></span>
      </div>
    `;

    return element;
  };

  const renderRoot = (root) => {
    const data = parseData(root);
    const inner = root.querySelector('.gift-with-purchase__inner');

    if (!data || !inner) {
      return;
    }

    const subtotal = Number(root.getAttribute('data-gift-with-purchase-subtotal')) || 0;
    const giftClaimVariantCounts = getGiftClaimVariantCounts();
    const renderedVariantCounts = new Map();
    const unlockedTiers = [];
    const lockedTiers = [];
    const fragment = document.createDocumentFragment();

    data.tiers.forEach((tier) => {
      const thresholdCents = getThresholdCents(tier);
      const variantId = String(tier.variant_id);
      const renderedVariantCount = renderedVariantCounts.get(variantId) || 0;

      if (!thresholdCents) {
        return;
      }

      if ((giftClaimVariantCounts.get(variantId) || 0) > renderedVariantCount) {
        renderedVariantCounts.set(variantId, renderedVariantCount + 1);
        return;
      }

      if (subtotal >= thresholdCents) {
        unlockedTiers.push(tier);
      } else {
        lockedTiers.push({ tier, thresholdCents });
      }

    });

    root.classList.toggle('gift-with-purchase--available', unlockedTiers.length > 0);
    root.classList.toggle('gift-with-purchase--locked', unlockedTiers.length === 0 && lockedTiers.length > 0);

    if (unlockedTiers.length > 0) {
      const availableList = document.createElement('scroll-carousel');

      availableList.className = 'gift-with-purchase__available-list scroll-area snap-x';
      availableList.setAttribute('allow-drag', '');
      availableList.setAttribute('use-relative-cell-offset', '');
      availableList.setAttribute('aria-label', data.i18n?.collapsed_title || 'Available gifts');

      if (unlockedTiers.length === 1) {
        availableList.classList.add('gift-with-purchase__available-list--single');
      }
      unlockedTiers.forEach((tier) => availableList.appendChild(createUnlockedTierElement(tier, data.i18n)));
      fragment.appendChild(availableList);
    } else if (lockedTiers.length > 0) {
      const nextLockedTier = lockedTiers.sort((a, b) => a.thresholdCents - b.thresholdCents)[0];
      fragment.appendChild(createLockedTierElement(nextLockedTier.tier, nextLockedTier.thresholdCents, subtotal, data.i18n));
    }

    inner.replaceChildren(fragment);
    root.hidden = inner.children.length === 0;
  };

  const renderAll = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach(renderRoot);
  };

  const refreshCart = () => {
    if (window.themeVariables?.settings?.pageType === 'cart') {
      window.location.href = `${getShopifyRoot()}cart`;
      return;
    }

    document.dispatchEvent(new CustomEvent('cart:refresh'));
  };

  const addGift = async (variantId, button) => {
    button?.setAttribute('aria-busy', 'true');
    button?.setAttribute('disabled', 'disabled');

    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle }
    }));

    const data = new FormData();
    data.append('id', variantId);
    data.append('properties[_gwp]', true);
    data.append('sections', sectionsToBundle.join(','));
    data.append('sections_url', window.location.pathname);

    try {
      const response = await fetch(`${getShopifyRoot()}cart/add.js`, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: data
      });
      const responseJson = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(responseJson.description || getErrorMessage());
      }

      const cartContent = await (await fetch(`${getShopifyRoot()}cart.js`)).json();
      cartContent.sections = responseJson.sections || {};

      if (window.themeVariables?.settings?.pageType === 'cart') {
        refreshCart();
      } else if (Object.keys(cartContent.sections).length > 0) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'variant:add',
            cart: cartContent
          }
        }));
      } else {
        refreshCart();
      }
    } catch (error) {
      console.error('Gift with purchase could not be added.', error);
      showError(error.message || getErrorMessage());
    } finally {
      button?.removeAttribute('aria-busy');
      button?.removeAttribute('disabled');
    }
  };

  const shouldKeepGiftClaim = (element) => {
    const root = document.querySelector(ROOT_SELECTOR);
    const data = root ? parseData(root) : null;
    const variantId = element.getAttribute('data-gift-variant-id');
    const subtotal = Number(root?.getAttribute('data-gift-with-purchase-subtotal')) || 0;

    if (!data || !variantId) {
      return false;
    }

    const claimedQuantity = getGiftClaimVariantCounts().get(variantId) || 0;
    const reachedTierCount = data.tiers.filter((tier) => {
      const thresholdCents = getThresholdCents(tier);

      return String(tier.variant_id) === variantId
        && thresholdCents !== null
        && subtotal >= thresholdCents;
    }).length;

    return claimedQuantity < reachedTierCount;
  };

  const removeInvalidGifts = async () => {
    const element = Array.from(document.querySelectorAll(INVALID_GIFT_SELECTOR)).find((candidate) => !candidate.hasAttribute(INITIALIZED_ATTRIBUTE));

    if (!element) {
      return;
    }

    const lineIndex = Number(element.getAttribute('data-line-index'));
    const lineKey = element.getAttribute('data-line-key');

    if (!Number.isInteger(lineIndex) || lineIndex < 1 || !lineKey) {
      showError(getErrorMessage());
      return;
    }

    if (shouldKeepGiftClaim(element)) {
      element.setAttribute(INITIALIZED_ATTRIBUTE, 'true');
      return;
    }

    element.setAttribute(INITIALIZED_ATTRIBUTE, 'true');

    try {
      const cart = await (await fetch(`${getShopifyRoot()}cart.js`, { cache: 'no-store' })).json();
      const currentLine = cart.items.find((item) => item.key === lineKey);

      if (!currentLine || currentLine.final_line_price === 0) {
        element.removeAttribute(INITIALIZED_ATTRIBUTE);
        refreshCart();
        return;
      }

      element.setAttribute('aria-hidden', 'true');
      element.style.display = 'none';

      const response = await fetch(`${getShopifyRoot()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 })
      });

      if (!response.ok) {
        throw new Error();
      }

      refreshCart();
    } catch (error) {
      console.error('Gift with purchase could not be removed.', error);
      element.removeAttribute(INITIALIZED_ATTRIBUTE);
      element.removeAttribute('aria-hidden');
      element.style.removeProperty('display');
      showError(getErrorMessage());
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-gift-with-purchase-add]');

    if (!button) {
      return;
    }

    event.preventDefault();
    addGift(
      button.getAttribute('data-gift-with-purchase-add'),
      button
    );
  });

  const sync = () => {
    removeInvalidGifts();
    renderAll();
  };

  document.addEventListener('DOMContentLoaded', sync);
  document.addEventListener('cart:change', sync);
  document.addEventListener('cart:refresh', sync);
  document.addEventListener('cart-drawer:refreshed', sync);
})();
